"use client"

import { useRef, useState } from "react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { ColumnFilter } from "@/components/ui/ColumnFilter"
import { cn } from "@/lib/utils"
import { updateWatchlistMemo } from "@/lib/api/watchlist"
import { RemoveFromWatchlistButton } from "@/components/watchlist/RemoveFromWatchlistButton"
import { ThemeBadges } from "@/components/shared/ThemeBadges"
import type { BacktestWatchlistItem, WatchlistFilterOptions } from "@/types/backtest"
import type { CurrentPrice } from "@/types/price"
import { Loader2 } from "lucide-react"

export type ColKey = "marketType" | "sector" | "conditionName" | "state"

interface Props<T extends BacktestWatchlistItem = BacktestWatchlistItem> {
  items: T[]
  isLoading: boolean
  selectedCode?: string | null
  onSelect: (item: T) => void
  currentPricesMap?: Record<string, CurrentPrice>
  today?: string
  renderAutoTradingColumn?: (item: T) => React.ReactNode
  // 시장/섹터/조건식/상태 필터 — 백엔드가 페이지네이션과 함께 필터링하므로
  // 옵션 목록·선택 상태·변경 핸들러를 부모(BacktestPage)로부터 받는다.
  filterOptions: WatchlistFilterOptions
  colFilters: Record<ColKey, Set<string>>
  onFilterChange: (key: ColKey, val: Set<string>) => void
}

function AuditBadge({ auditInfo }: { auditInfo: string | null }) {
  if (auditInfo === "정상")
    return <Badge variant="outline" className="border-emerald-500/40 bg-emerald-500/10 text-emerald-400">정상</Badge>
  if (auditInfo === "투자주의")
    return <Badge variant="outline" className="border-yellow-500/40 bg-yellow-500/10 text-yellow-400">투자주의</Badge>
  if (auditInfo === "거래정지")
    return <Badge variant="outline" className="border-red-500/40 bg-red-500/10 text-red-400">거래정지</Badge>
  return (
    <Badge variant="outline" className="border-zinc-500/40 bg-zinc-500/10 text-zinc-400">
      {auditInfo ?? "-"}
    </Badge>
  )
}

const MA_STATUS_BADGE: Record<string, string> = {
  "정배열":     "border-red-500/40    bg-red-500/10    text-red-400",
  "역배열":     "border-blue-500/40   bg-blue-500/10   text-blue-400",
  "혼조":       "border-yellow-500/40 bg-yellow-500/10 text-yellow-400",
  "데이터부족": "border-zinc-500/40   bg-zinc-500/10    text-zinc-400",
}

function MovingAverageBadge({ status }: { status: string }) {
  return (
    <Badge variant="outline" className={MA_STATUS_BADGE[status] ?? MA_STATUS_BADGE["데이터부족"]}>
      {status}
    </Badge>
  )
}

function OrderWarningBadges({ orderWarning }: { orderWarning: string | null }) {
  const w = orderWarning ?? ""
  const badges = []
  if (w.includes("관리"))
    badges.push(<Badge key="managed" variant="outline" className="border-yellow-500/40 bg-yellow-500/10 text-yellow-400">관리</Badge>)
  if (w.includes("위험"))
    badges.push(<Badge key="danger" variant="outline" className="border-red-500/40 bg-red-500/10 text-red-400">위험</Badge>)
  if (w.includes("경고"))
    badges.push(<Badge key="warning" variant="outline" className="border-orange-500/40 bg-orange-500/10 text-orange-400">경고</Badge>)
  if (w.includes("주의"))
    badges.push(<Badge key="caution" variant="outline" className="border-orange-500/40 bg-orange-500/10 text-orange-400">주의</Badge>)
  return badges.length > 0 ? <>{badges}</> : null
}

function fmt(n: number | null | undefined): string {
  if (n == null || n === 0) return "-"
  return n.toLocaleString("ko-KR")
}

function fmtAmount(n: number | null | undefined): string {
  if (n == null || n === 0) return "-"
  const eok = Math.round(n / 100_000_000)
  return eok > 0 ? `${eok.toLocaleString("ko-KR")}억` : n.toLocaleString("ko-KR")
}

// 전일종가 대비 등락률(%) — chart 페이지(CandleChart)와 동일한 계산/표시 방식
function pct(value: number | null | undefined, prev: number | null | undefined): number | null {
  if (value == null || prev == null || prev === 0) return null
  return ((value - prev) / prev) * 100
}

function fmtRate(rate: number): string {
  return `${rate >= 0 ? "+" : ""}${rate.toFixed(2)}%`
}

function rateColor(rate: number): string {
  if (rate > 0) return "text-red-400"
  if (rate < 0) return "text-blue-400"
  return "text-zinc-400"
}

function PriceCell({ value, prevClose }: { value: number | null | undefined; prevClose: number | null | undefined }) {
  const rate = value != null && value !== 0 ? pct(value, prevClose) : null
  return (
    <>
      {fmt(value)}
      {rate != null && <span className={cn("ml-1 text-xs", rateColor(rate))}>({fmtRate(rate)})</span>}
    </>
  )
}

interface DisplayPrices {
  highPrice:   number | null
  lowPrice:    number | null
  closePrice:  number | null
  volume:      number | null
  tradeAmount: number | null
}

function getDisplayPrices(
  item: BacktestWatchlistItem,
  live?: CurrentPrice,
): DisplayPrices {
  if (live) {
    return {
      highPrice:   live.highPric,
      lowPrice:    live.lowPric,
      closePrice:  live.currentPrice,
      volume:      live.tradeQty,
      tradeAmount: live.tradePrica,
    }
  }
  return {
    highPrice:   item.highPrice,
    lowPrice:    item.lowPrice,
    closePrice:  item.closePrice,
    volume:      item.volume,
    tradeAmount: item.tradeAmount,
  }
}

function closeColor(d: DisplayPrices): string {
  if (d.closePrice == null || d.highPrice == null || d.lowPrice == null)
    return "text-zinc-200"
  const mid = (d.highPrice + d.lowPrice) / 2
  return d.closePrice >= mid ? "text-red-400" : "text-blue-400"
}

function MemoCell({
  item,
  displayMemo,
  onSaved,
}: {
  item: BacktestWatchlistItem
  displayMemo: string | null
  onSaved: (memo: string) => void
}) {
  const [editing, setEditing] = useState(false)
  const [value,   setValue]   = useState("")
  const [saving,  setSaving]  = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const startEdit = (e: React.MouseEvent) => {
    e.stopPropagation()
    setValue(displayMemo ?? "")
    setEditing(true)
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  const save = async () => {
    setEditing(false)
    const next = value.trim()
    if (next === (displayMemo ?? "")) return
    setSaving(true)
    try {
      await updateWatchlistMemo({
        stockCode:     item.stockCode,
        searchedDate:  item.searchedDate,
        conditionName: item.conditionName,
        memo:          next,
      })
      onSaved(next)
    } catch {
      // 실패 시 원래 값 유지
    } finally {
      setSaving(false)
    }
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter")  { e.preventDefault(); save() }
    if (e.key === "Escape") { setEditing(false) }
  }

  if (saving) {
    return (
      <span className="flex items-center gap-1 text-xs text-zinc-500">
        <Loader2 className="size-3 animate-spin" />
        저장 중…
      </span>
    )
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={save}
        onKeyDown={onKeyDown}
        onClick={(e) => e.stopPropagation()}
        className="w-full rounded border border-zinc-600 bg-zinc-800 px-1.5 py-0.5 text-xs text-zinc-100 outline-none focus:border-emerald-500"
        placeholder="메모 입력…"
      />
    )
  }

  return (
    <span
      onClick={startEdit}
      className={cn(
        "block min-w-[60px] cursor-text rounded px-1 py-0.5 text-xs hover:bg-zinc-800",
        displayMemo ? "text-zinc-300" : "text-zinc-600"
      )}
      title="클릭하여 메모 편집"
    >
      {displayMemo || "-"}
    </span>
  )
}

function SkeletonRows({ columns }: { columns: number }) {
  return (
    <>
      {Array.from({ length: 6 }).map((_, i) => (
        <TableRow key={i} className="border-zinc-800">
          {Array.from({ length: columns }).map((_, j) => (
            <TableCell key={j}>
              <Skeleton className="h-4 w-full bg-zinc-800" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  )
}

export function WatchlistStockTable<T extends BacktestWatchlistItem = BacktestWatchlistItem>({
  items,
  isLoading,
  selectedCode,
  onSelect,
  currentPricesMap,
  today,
  renderAutoTradingColumn,
  filterOptions,
  colFilters,
  onFilterChange,
}: Props<T>) {
  const totalCols = 18 + (renderAutoTradingColumn ? 1 : 0)
  const displayed = items

  // 로컬 메모 오버라이드 (저장 완료 후 즉시 반영)
  const [memoMap, setMemoMap] = useState<Record<string, string>>({})
  const memoKey = (item: BacktestWatchlistItem) => `${item.stockCode}-${item.searchedDate}-${item.conditionName}`
  const getMemo = (item: BacktestWatchlistItem) => memoKey(item) in memoMap ? memoMap[memoKey(item)] : (item.memo ?? null)
  const handleMemoSaved = (item: BacktestWatchlistItem, memo: string) =>
    setMemoMap((prev) => ({ ...prev, [memoKey(item)]: memo }))

  return (
    <>
      {/* 데스크탑: 테이블 — 화면이 좁아도 컬럼이 눌리지 않도록 테이블 자체에
          min-width를 지정해 가로 스크롤을 유도한다 (Table 컴포넌트가 내부에
          overflow-x-auto 래퍼를 이미 갖고 있음).
          [&_[data-slot=table-container]]: shadcn Table의 내부 스크롤 래퍼에
          부모로부터 내려받은 높이(h-full)와 overflow-y-auto를 얹어서, 가로
          스크롤바가 항상 "현재 보이는 테이블 영역 하단"에 고정되도록 한다
          (해당 래퍼는 ui/table.tsx 자동 생성 파일이라 직접 수정하지 않음) */}
      <div className="hidden h-full md:block [&_[data-slot=table-container]]:h-full [&_[data-slot=table-container]]:overflow-y-auto">
        <Table className="min-w-[2000px]">
          <TableHeader>
            <TableRow className="border-zinc-800 hover:bg-transparent">
              <TableHead className="w-16 text-zinc-400">
                <div className="flex items-center gap-1">
                  시장
                  <ColumnFilter
                    values={filterOptions.marketTypes}
                    selected={colFilters.marketType}
                    onChange={(v) => onFilterChange("marketType", v)}
                  />
                </div>
              </TableHead>
              <TableHead className="w-28 text-zinc-400">
                <div className="flex items-center gap-1">
                  섹터
                  <ColumnFilter
                    values={filterOptions.sectors}
                    selected={colFilters.sector}
                    onChange={(v) => onFilterChange("sector", v)}
                  />
                </div>
              </TableHead>
              <TableHead className="w-28 text-zinc-400">업종</TableHead>
              <TableHead className="w-48 text-zinc-400">테마</TableHead>
              <TableHead className="w-24 text-zinc-400">검색날짜</TableHead>
              <TableHead className="w-20 text-zinc-400">
                <div className="flex items-center gap-1">
                  조건식
                  <ColumnFilter
                    values={filterOptions.conditionNames}
                    selected={colFilters.conditionName}
                    onChange={(v) => onFilterChange("conditionName", v)}
                  />
                </div>
              </TableHead>
              <TableHead className="w-20 text-zinc-400">종목코드</TableHead>
              <TableHead className="w-32 text-zinc-400">종목명</TableHead>
              <TableHead className="w-32 text-right text-zinc-400">고가</TableHead>
              <TableHead className="w-32 text-right text-zinc-400">저가</TableHead>
              <TableHead className="w-32 text-right text-zinc-400">종가</TableHead>
              <TableHead className="w-24 text-right text-zinc-400">거래대금</TableHead>
              <TableHead className="w-24 text-right text-zinc-400">거래량</TableHead>
              <TableHead className="w-28 text-right text-zinc-400">유동주식수×1.5</TableHead>
              <TableHead className="w-24 text-zinc-400">
                <div className="flex items-center gap-1">
                  상태
                  <ColumnFilter
                    values={filterOptions.states}
                    selected={colFilters.state}
                    onChange={(v) => onFilterChange("state", v)}
                  />
                </div>
              </TableHead>
              <TableHead className="w-24 text-zinc-400">이평배열</TableHead>
              <TableHead className="w-32 text-zinc-400">메모</TableHead>
              <TableHead className="w-10 text-zinc-400" />
              {renderAutoTradingColumn && <TableHead className="w-28 text-zinc-400">자동매매</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <SkeletonRows columns={totalCols} />
            ) : displayed.length === 0 ? (
              <TableRow className="border-zinc-800">
                <TableCell colSpan={totalCols} className="py-12 text-center text-zinc-500">
                  결과가 없습니다.
                </TableCell>
              </TableRow>
            ) : (
              displayed.map((item) => {
                const isToday = today != null && item.searchedDate === today
                const live    = isToday ? currentPricesMap?.[item.stockCode] : undefined
                const d       = getDisplayPrices(item, live)
                return (
                  <TableRow
                    key={item.id}
                    className={cn(
                      "cursor-pointer border-zinc-800",
                      selectedCode === item.stockCode
                        ? "bg-emerald-500/10 text-zinc-100"
                        : "text-zinc-200 hover:bg-zinc-800/60"
                    )}
                    onClick={() => onSelect(item)}
                  >
                    <TableCell className="text-xs text-zinc-400">{item.marketType}</TableCell>
                    <TableCell className="max-w-[112px] truncate text-xs text-zinc-400">
                      {item.sector ?? "-"}
                    </TableCell>
                    <TableCell className="max-w-[112px] truncate text-xs text-zinc-400">
                      {item.industry ?? "-"}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <ThemeBadges themes={item.themes} />
                    </TableCell>
                    <TableCell className="text-xs text-zinc-400">{item.searchedDate}</TableCell>
                    <TableCell className="text-xs text-zinc-400">{item.conditionName}</TableCell>
                    <TableCell className="font-mono text-xs text-zinc-400">{item.stockCode}</TableCell>
                    <TableCell className="font-medium">{item.stockName}</TableCell>
                    <TableCell className="text-right font-mono text-sm text-red-400">
                      <PriceCell value={d.highPrice} prevClose={item.prevClosePrice} />
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm text-blue-400">
                      <PriceCell value={d.lowPrice} prevClose={item.prevClosePrice} />
                    </TableCell>
                    <TableCell className={cn("text-right font-mono text-sm font-semibold", closeColor(d))}>
                      <PriceCell value={d.closePrice} prevClose={item.prevClosePrice} />
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm text-zinc-300">
                      {fmtAmount(d.tradeAmount)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm text-zinc-300">
                      {fmt(d.volume)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm text-zinc-300">
                      {item.floatShares != null
                        ? Math.round(item.floatShares * 1.5).toLocaleString("ko-KR")
                        : "-"}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <AuditBadge auditInfo={item.auditInfo} />
                        <OrderWarningBadges orderWarning={item.orderWarning} />
                      </div>
                    </TableCell>
                    <TableCell>
                      <MovingAverageBadge status={item.movingAverageStatus} />
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <MemoCell
                        item={item}
                        displayMemo={getMemo(item)}
                        onSaved={(memo) => handleMemoSaved(item, memo)}
                      />
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {item.source === "manual" && (
                        <RemoveFromWatchlistButton stockCode={item.stockCode} stockName={item.stockName} />
                      )}
                    </TableCell>
                    {renderAutoTradingColumn && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {renderAutoTradingColumn(item)}
                      </TableCell>
                    )}
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* 모바일: 카드형 리스트 */}
      <div className="divide-y divide-zinc-800 md:hidden">
        {isLoading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex flex-col gap-2 px-4 py-3">
              <Skeleton className="h-4 w-2/3 bg-zinc-800" />
              <Skeleton className="h-3 w-1/2 bg-zinc-800" />
            </div>
          ))
        ) : displayed.length === 0 ? (
          <div className="py-12 text-center text-zinc-500">결과가 없습니다.</div>
        ) : (
          displayed.map((item) => {
            const isToday = today != null && item.searchedDate === today
            const live    = isToday ? currentPricesMap?.[item.stockCode] : undefined
            const d       = getDisplayPrices(item, live)
            return (
              <div
                key={item.id}
                className={cn(
                  "flex min-h-[52px] cursor-pointer items-center justify-between gap-3 px-4 py-3 active:opacity-80",
                  selectedCode === item.stockCode ? "bg-emerald-500/10" : ""
                )}
                onClick={() => onSelect(item)}
              >
                <div className="flex min-w-0 flex-col gap-0.5">
                  <div className="flex min-w-0 items-center gap-1.5">
                    <span className="truncate font-medium text-zinc-100">{item.stockName}</span>
                    <MovingAverageBadge status={item.movingAverageStatus} />
                  </div>
                  <span className="font-mono text-xs text-zinc-500">
                    {item.stockCode} · {item.searchedDate} · {item.conditionName}
                  </span>
                  {(item.sector || item.industry) && (
                    <span className="text-xs text-zinc-600 truncate">
                      {[item.sector, item.industry].filter(Boolean).join(" · ")}
                    </span>
                  )}
                  {item.themes.length > 0 && (
                    <div onClick={(e) => e.stopPropagation()}>
                      <ThemeBadges themes={item.themes} />
                    </div>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <div className="flex flex-col items-end gap-0.5">
                    <span className={cn("font-mono text-sm font-semibold", closeColor(d))}>
                      <PriceCell value={d.closePrice} prevClose={item.prevClosePrice} />
                    </span>
                    <span className="text-xs text-zinc-500">{fmtAmount(d.tradeAmount)}</span>
                  </div>
                  {item.source === "manual" && (
                    <RemoveFromWatchlistButton stockCode={item.stockCode} stockName={item.stockName} />
                  )}
                  {renderAutoTradingColumn && (
                    <div onClick={(e) => e.stopPropagation()}>{renderAutoTradingColumn(item)}</div>
                  )}
                </div>
              </div>
            )
          })
        )}
      </div>
    </>
  )
}
